import type { IntlShape } from "react-intl";
import type { DateRange } from "react-day-picker";

/** Locale-aware date formatter using the locale from react-intl. */
export function formatDateLocale(intl: IntlShape, date: Date | string): string {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(intl.locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(dateObj);
}

/** Start of local calendar day as UTC ISO (for API filters). */
export function dateToStartOfDayUTC(date: Date): string {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)).toISOString();
}

/** End of local calendar day as UTC ISO (for API filters). */
export function dateToEndOfDayUTC(date: Date): string {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)).toISOString();
}

const CALENDAR_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Validate a `YYYY-MM-DD` URL token: shape must match and the day must exist on the
 * local calendar (rejects e.g. 2024-02-30, which `Date` would roll forward).
 */
export function isValidCalendarDay(day: string): boolean {
  const match = CALENDAR_DAY_RE.exec(day);
  if (!match) return false;

  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const date = new Date(y, m - 1, d);

  return (
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
  );
}

/** Local Date → `YYYY-MM-DD` for URL search. */
export function toCalendarDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** `YYYY-MM-DD` → local midnight Date. */
export function parseCalendarDay(day: string): Date {
  if (!isValidCalendarDay(day)) {
    throw new Error(`Invalid calendar day: ${day}`);
  }
  const [, y, m, d] = CALENDAR_DAY_RE.exec(day)!;
  return new Date(Number(y), Number(m) - 1, Number(d));
}

/** URL calendar days → react-day-picker DateRange. */
export function calendarDaysToDateRange(from?: string, to?: string): DateRange | undefined {
  if (!from && !to) return undefined;
  return {
    from: from ? parseCalendarDay(from) : undefined,
    to: to ? parseCalendarDay(to) : undefined,
  };
}

/** URL calendar day → API start-of-day ISO. */
export function calendarDayToStartISO(day?: string): string | undefined {
  return day ? dateToStartOfDayUTC(parseCalendarDay(day)) : undefined;
}

/** URL calendar day → API end-of-day ISO. */
export function calendarDayToEndISO(day?: string): string | undefined {
  return day ? dateToEndOfDayUTC(parseCalendarDay(day)) : undefined;
}

/** ISO timestamp → epoch ms. Returns 0 when the value is missing or unparseable. */
export function isoToEpochMs(value: string | null | undefined): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}
